<!-- MainNavbar.vue -->
<template>
  <div>
    <!-- Main Navigation Bar -->
    <nav class="navbar navbar-expand-md custom-fixed-navbar py-0">
      <div class="container">
        <!-- Navbar Toggler (Three-dots icon) -->
        <button
          class="navbar-toggler me-2 text-white"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#mainNavbarContent"
          aria-controls="mainNavbarContent"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span class="bi bi-three-dots"></span>
        </button>

        <!-- Content Navigation Toggle (if applicable) -->
        <button
          v-if="showContentNav"
          class="navbar-toggler text-white"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#contentNavbar"
          aria-controls="contentNavbar"
          aria-expanded="false"
          aria-label="Toggle content navigation"
        >
          <span class="navbar-toggler-icon"></span>
        </button>

        <!-- Main Navigation Links -->
        <div class="collapse navbar-collapse justify-content-md-center" id="mainNavbarContent">
          <ul class="navbar-nav fw-semibold fs-5 text-white">
            <li class="nav-item">
              <router-link class="nav-link" to="/">Home</router-link>
            </li>
            <li class="nav-item">
              <router-link class="nav-link" to="/tool">Tool</router-link>
            </li>
          </ul>
        </div>

        <!-- Login Status Indicator -->
        <div class="d-flex align-items-center">
          <!-- If the user is logged in -->
          <div v-if="authStore.isLoggedIn" class="nav-item">
            <a class="nav-link text-white d-flex align-items-center" href="#" @click.prevent="logout">
              <i class="bi bi-box-arrow-right me-1"></i> Logout
            </a>
          </div>
          <!-- If the user is not logged in -->
          <div v-else class="nav-item">
            <router-link class="nav-link text-white d-flex align-items-center" to="/login">
              <i class="bi bi-box-arrow-in-right me-1"></i> Login
            </router-link>
          </div>
        </div>
      </div>
    </nav>

    <!-- Content Sections Navbar (small screens) -->
    <div v-if="showContentNav" class="collapse content-navbar" id="contentNavbar">
      <ul class="navbar-nav flex-column">
        <li v-for="(section, index) in sections" :key="index" class="nav-item">
          <a class="nav-link" :href="`#${section.ref}`">{{ section.title }}</a>
          <ul v-if="section.subsections" class="navbar-nav flex-column ms-3">
            <li v-for="(sub, subIndex) in section.subsections" :key="subIndex" class="nav-item">
              <a class="nav-link" :href="`#${sub.ref}`">{{ sub.title }}</a>
            </li>
          </ul>
        </li>
      </ul>
    </div>

    <!-- Sidebar (sichtbar auf großen Bildschirmen) -->
    <nav v-if="showContentNav" class="d-none d-md-block sidebar">
      <ul class="navbar-nav flex-column">
        <li v-for="(section, index) in sections" :key="index" class="nav-item">
          <a class="nav-link sidebar-link" :href="`#${section.ref}`">{{ section.title }}</a>
          <ul v-if="section.subsections" class="navbar-nav flex-column ms-3">
            <li v-for="(sub, subIndex) in section.subsections" :key="subIndex" class="nav-item">
              <a class="nav-link sidebar-link" :href="`#${sub.ref}`">{{ sub.title }}</a>
            </li>
          </ul>
        </li>
      </ul>
    </nav>
  </div>
</template>



<script>
import { useAuthStore } from '../stores/auth'; // Import the Pinia store
import { onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useRouter } from 'vue-router'; // Import useRouter for navigation

export default {
  name: 'MainNavbar',
  props: {
    showContentNav: {
      type: Boolean,
      default: false,
    },
    sections: {
      type: Array,
      default: () => [],
    },
  },
  setup() {
    const router = useRouter();
    const authStore = useAuthStore(); // Get the auth store instance

    // Adjust positions based on header and navbar heights
    const adjustPositions = () => {
      nextTick(() => {
        const header = document.querySelector('header');
        const navbar = document.querySelector('.custom-fixed-navbar');
        const sidebar = document.querySelector('.sidebar');
        if (header && navbar) {
          const headerHeight = header.offsetHeight;
          navbar.style.top = `${headerHeight}px`;
        }
        if (header && navbar && sidebar) {
          const headerHeight = header.offsetHeight;
          const navbarHeight = navbar.offsetHeight;
          sidebar.style.top = `${headerHeight + navbarHeight}px`;
          sidebar.style.height = `calc(100vh - ${headerHeight + navbarHeight}px)`;
        }
      });
    };

    // Handle window resize events
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        const contentNavbar = document.getElementById('contentNavbar');
        if (contentNavbar && contentNavbar.classList.contains('show')) {
          const toggler = document.querySelector('[data-bs-target="#contentNavbar"]');
          if (toggler) {
            toggler.click();
          }
        }
      }
    };

    onMounted(() => {
      adjustPositions();
      window.addEventListener('resize', handleResize);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleResize);
    });

    const logout = () => {
      authStore.logout(); // Use the store's logout action
      router.push('/login');
    };

    return {
      authStore,
      logout,
    };
  },
};
</script>



<style scoped>
.custom-fixed-navbar {
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 1020;
  background-color: #87b140;
}

/* Ausklappbares Menü unter der Navbar fixieren */
.content-navbar {
  position: fixed;
  top: 60px; /* Abstand entsprechend der Höhe der Navbar einstellen */
  left: 0;
  width: 100%;
  z-index: 1010;
  background-color: #f2f5e7; /* Hintergrundfarbe für das ausgeklappte Menü */
  padding-top: 60px;
}

/* Grüner Rand am unteren Ende des ausgeklappten Menüs mit Abstand links und rechts */
.content-navbar.collapse.show::after {
  content: "";
  display: block;
  width: 90%; /* Balkenbreite reduziert für links/rechts Abstand */
  margin: 0 auto; /* Zentriert den Balken */
  border-bottom: 3px solid #87b140;
  margin-top: 10px;
}

/* Weißer Hamburger-Icon-Farbe mit Filter */
.custom-fixed-navbar .navbar-toggler {
  border-color: rgba(255, 255, 255, 1);
}

.custom-fixed-navbar .navbar-toggler-icon {
  filter: brightness(0) invert(1);
}

.navbar .nav-link {
  color: #ffffff !important;
  display: flex;
  align-items: center;
}

.navbar .nav-link i {
  font-size: 1.2rem;
}


/* Schriftfarbe im ausgeklappten Menü auf Schwarz setzen */
#contentNavbar .nav-link {
  color: #000000 !important;
}

.sidebar {
  position: fixed;
  top: 0;
  left: 0;
  width: 250px;
  padding-top: 1.5rem;
  overflow-y: auto;
  z-index: 1010;
  background-color: transparent;
  margin-top: 20px;
  border-right: 3px solid #87b140;
}

.sidebar .sidebar-link {
  color: #000000 !important;
  padding: 10px 10px 10px 30px;
  text-decoration: none;
}

.sidebar .sidebar-link:hover,
#contentNavbar .nav-link:hover {
  background-color: #87b140;
  color: #ffffff !important;
  border-radius: 1px;
}

.nav-link:not(.sidebar-link) {
  color: #ffffff !important;
  padding: 10px;
  display: block;
  text-align: left;
}

.content-with-sidebar {
  margin-left: 270px;
}

@media (max-width: 768px) {
  .content-with-sidebar {
    margin-left: 0;
  }
}
</style>
